import { observer } from 'mobx-react-lite';
import { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from '@shared/utils/i18n';
import { CombinedAct } from '@shared/types/osi/acts';
import { OsiActsWidgetViewModel } from '@widgets/osiActs/model/viewModel';
import { CombinedActItemsTable } from '@widgets/osiActs/ui/combined/CombinedActItemsTable';
import { formatActAmount, formatActPeriod } from '@widgets/osiActs/ui/combined/format';
import { getRuPluralForm, getVisibleSignedCombinedActs } from '@widgets/osiActs/ui/combined/signedList';

interface Props {
  viewModel: OsiActsWidgetViewModel;
}

/** OSI-818: сводка общего акта — период, БИН/ИИН, число домов, итоговая сумма. */
const CombinedActSummary: React.FC<{ combinedAct: CombinedAct }> = ({ combinedAct }) => {
  const { t } = useTranslation();

  const fields = [
    { label: t('acts:combinedAct.period'), value: formatActPeriod(combinedAct.actPeriod) },
    { label: t('acts:combinedAct.idn'), value: combinedAct.idn },
    { label: t('acts:combinedAct.housesCount'), value: combinedAct.items?.length ?? 0 },
    { label: t('acts:combinedAct.totalAmount'), value: formatActAmount(combinedAct.totalAmount) }
  ];

  if (combinedAct.signDt) {
    fields.push({
      label: t('acts:combinedAct.signDate'),
      value: new Date(combinedAct.signDt).toLocaleString('ru-RU')
    });
  }

  return (
    <Grid container spacing={2}>
      {fields.map((field) => (
        <Grid item xs={6} md key={field.label}>
          <Typography variant="caption" color="text.secondary">
            {field.label}
          </Typography>
          <Typography variant="subtitle1">{field.value}</Typography>
        </Grid>
      ))}
    </Grid>
  );
};

interface SignedCombinedActRowProps {
  combinedAct: CombinedAct;
  currentOsiId: number | null;
  onView: () => void;
}

/**
 * OSI-818: строка подписанного общего акта — свёрнутый Accordion.
 * Заголовок: период · «N домов» · итоговая сумма · «подписан ДД.ММ.ГГГГ» · «Просмотр».
 * На узком экране элементы заголовка переносятся (flexWrap), горизонтальной прокрутки нет.
 * Таблица состава монтируется только при раскрытии (unmountOnExit) — 6+ таблиц по 20–40
 * строк не рендерятся зря.
 */
const SignedCombinedActRow: React.FC<SignedCombinedActRowProps> = ({ combinedAct, currentOsiId, onView }) => {
  const { t } = useTranslation();
  const housesCount = combinedAct.items?.length ?? 0;

  return (
    <Accordion variant="outlined" disableGutters TransitionProps={{ unmountOnExit: true }}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          '& .MuiAccordionSummary-content': {
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            columnGap: 2,
            rowGap: 0.5,
            minWidth: 0
          }
        }}
      >
        <Typography variant="subtitle2" sx={{ textTransform: 'capitalize' }}>
          {formatActPeriod(combinedAct.actPeriod)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t(`acts:combinedAct.houses.${getRuPluralForm(housesCount)}`, { count: housesCount })}
        </Typography>
        <Typography variant="body2">{formatActAmount(combinedAct.totalAmount)}</Typography>
        {combinedAct.signDt && (
          <Typography variant="body2" color="text.secondary">
            {t('acts:combinedAct.signedAt', { date: new Date(combinedAct.signDt).toLocaleDateString('ru-RU') })}
          </Typography>
        )}
        <Box sx={{ ml: 'auto' }}>
          <Button
            size="small"
            variant="outlined"
            // Клик по кнопке не должен сворачивать/разворачивать строку: AccordionSummary
            // слушает click на всём заголовке, поэтому гасим всплытие.
            onClick={(event) => {
              event.stopPropagation();
              onView();
            }}
            onFocus={(event) => event.stopPropagation()}
          >
            {t('common:view')}
          </Button>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <CombinedActItemsTable items={combinedAct.items ?? []} currentOsiId={currentOsiId} />
      </AccordionDetails>
    </Accordion>
  );
};

/**
 * OSI-818: блок «Общий акт по БИН/ИИН» над таблицами актов.
 * Неподписанные общие акты — развёрнуто, с составом и кнопкой «Подписать общий акт»;
 * подписанные — в свёрнутом Accordion с кнопкой «Просмотр».
 * Если общих актов у дома нет, блок не выводится вовсе.
 */
export const CombinedActs: React.FC<Props> = observer(({ viewModel }) => {
  const { t } = useTranslation();
  const { notSignedCombinedActs, signedCombinedActs, currentOsiId, openingCombinedActId } = viewModel;
  // OSI-818: «Показать ещё» / «Свернуть» для списка подписанных общих актов.
  const [showAllSigned, setShowAllSigned] = useState(false);
  const signedList = useMemo(
    () => getVisibleSignedCombinedActs(signedCombinedActs, showAllSigned),
    [signedCombinedActs, showAllSigned]
  );

  if (!notSignedCombinedActs.length && !signedCombinedActs.length) return null;

  const isOpening = openingCombinedActId !== null;

  return (
    <Box sx={{ mt: 2, mb: 2 }}>
      <Card>
        <CardHeader
          subheader={
            <Typography variant="h5" color="textPrimary">
              {t('acts:combinedAct.title')}
            </Typography>
          }
        />
        <CardContent>
          <Stack spacing={3} divider={<Divider flexItem />}>
            {notSignedCombinedActs.map((combinedAct) => (
              <Stack key={combinedAct.id} spacing={2}>
                <CombinedActSummary combinedAct={combinedAct} />
                <CombinedActItemsTable items={combinedAct.items ?? []} currentOsiId={currentOsiId} />
                <Box>
                  <Button
                    variant="contained"
                    disabled={isOpening}
                    startIcon={
                      openingCombinedActId === combinedAct.id ? <CircularProgress size={16} color="inherit" /> : null
                    }
                    onClick={() => {
                      void viewModel.onOpenCombinedAct(combinedAct.id);
                    }}
                  >
                    {t('acts:combinedAct.signButton')}
                  </Button>
                </Box>
              </Stack>
            ))}

            {/* OSI-818: подписанные общие акты — компактный список внутри свёрнутого блока:
                строка-заголовок на акт, состав только в раскрытой строке, по умолчанию
                последние SIGNED_COMBINED_ACTS_LIMIT, остальные — по «Показать ещё». */}
            {signedCombinedActs.length > 0 && (
              <Accordion defaultExpanded={false} disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">
                    {t('acts:combinedAct.signedTitle', { count: signedCombinedActs.length })}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={1}>
                    {signedList.visible.map((combinedAct) => (
                      <SignedCombinedActRow
                        key={combinedAct.id}
                        combinedAct={combinedAct}
                        currentOsiId={currentOsiId}
                        onView={() => {
                          void viewModel.onShowSignedCombinedAct(combinedAct.id);
                        }}
                      />
                    ))}
                  </Stack>
                  {signedList.hasMore && (
                    <Box sx={{ mt: 1 }}>
                      <Button size="small" onClick={() => setShowAllSigned((value) => !value)}>
                        {showAllSigned
                          ? t('acts:combinedAct.showLess')
                          : t('acts:combinedAct.showMore', { count: signedList.hiddenCount })}
                      </Button>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
});
